import React, { createRef, forwardRef, RefObject, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Translate } from '@ff-os/shared-utils/types/Translate';
import { bytesToMb } from '@ff-os/shared-utils/units';
import classNames from 'classnames';
import { noop } from 'lodash';
import { isSupportedByBrowser, isValidButUnsupportedByBrowser } from '../../dom/images/image-browser-support';
import styles from './FileUploader.module.scss';

export const FileUploader = forwardRef<FileUploaderForward, Props>(({
    className,
    onChange, onError = noop,
    maxSize, multiple = false,
    accept = 'image/png, image/jpeg, image/bmp, image/heic, image/heif'
}, ref) => {

    const { t } = useTranslation();

    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => ({
        browse: () => inputRef.current?.click(),
        upload: (f) => handleInput(f)
    }));

    const handleInput = (files: File[]) => {

        if (files.length === 0) {
            return;
        }

        const { errors, passedFiles } = validate(files, accept, maxSize, multiple, t);

        if (passedFiles.length > 0) {
            onChange(passedFiles);
        }

        if (errors.length > 0) {
            onError(errors);
        }
    };

    return (
        <input type='file' name='files[]' id='imageUpload' ref={inputRef}
            className={classNames(className, styles.input)} multiple={multiple}
            onClick={clearInput}
            onTouchStart={clearInput}
            onChange={e => handleInput([...e.target.files ?? []])}
            accept={accept} />
    );
});

// Fix resubmitting files
const clearInput = (
    event: React.MouseEvent<HTMLInputElement, MouseEvent> | React.TouchEvent<HTMLInputElement>
) => {
    const target = event.target as HTMLInputElement;
    target.value = '';
};

export type FileUploaderForward = {
    browse: () => void;
    upload: (files: File[]) => void;
};

export type UploadError = {
    type: 'file' | 'input';
    text: string;
};

export const useFileUploader = (forwardedRef: React.ForwardedRef<FileUploaderForward>) => {
    const ref = forwardedRef as RefObject<FileUploaderForward>;
    return {
        upload: (files: File[]) => ref?.current?.upload(files),
        browse: () => ref?.current?.browse()
    };
};

export const createFileUploaderRef = () => createRef<FileUploaderForward>();

type Props = {
    onChange: (files: File[]) => void;
    onError?: (errors: UploadError[]) => void;
    maxSize: number;
    accept?: string;
    multiple?: boolean;
    className?: string;
};

const validate = (
    files: File[],
    accept: string,
    maxSize: number,
    multiple: boolean,
    t: Translate
) => {

    const errors: UploadError[] = [];

    if (!multiple && files.length > 1) {
        errors.push({
            type: 'input',
            text: t('dropzone.error.exceeds_file_limit')
        });
    }

    const isTypeAccepted = (file: File) => isSupportedByBrowser(file)
        ? accept.includes(file.type)
        : isValidButUnsupportedByBrowser(file);

    const invalidFiles = files.filter(f => !isTypeAccepted(f));

    for (const file of invalidFiles) {
        errors.push({
            type: 'file',
            text: t('dropzone.error.invalid_files', { fileName: limitFileNameLength(file.name) })
        });
    }

    const filesThatAreTooLarge = files.filter(f => f.size > maxSize);
    for (const file of filesThatAreTooLarge) {

        const text = t('dropzone.error.files_too_large', {
            fileName: limitFileNameLength(file.name),
            maxSize: maxSize * bytesToMb
        });

        errors.push({ type: 'file', text: text });
    }

    const failedFiles = invalidFiles
        .concat(...filesThatAreTooLarge.filter(f => !invalidFiles.includes(f)));

    const passedFiles = failedFiles.length > 0
        ? files.filter(f => !failedFiles.includes(f))
        : files;

    return {
        errors,
        passedFiles
    };
};

const limitFileNameLength = (fileName: string) => {

    const extensionStartAt = fileName.lastIndexOf('.');
    const extension = fileName.substring(extensionStartAt + 1);
    const start = fileName.substring(0, Math.min(20 - extension.length, extensionStartAt));

    if (extension.length + extensionStartAt <= 20) {
        return fileName;
    } else {
        return `${start}...${extension}`;
    }
};